/**
 * Stable identity for a size row.
 *
 * An item priced by size carries its sizes as an ARRAY ON THE ITEM, not as
 * documents. Items and modifiers have both a numeric id and a `guid`; a size had
 * neither in practice — `ISizePrice.guid` has always been declared, and nothing
 * has ever written it. So the only thing that identified a size was its NAME.
 *
 * A name is not an identity. Rename "Large" to "Lg" and every external reference
 * to that size — a published marketplace menu, an in-flight order, a saved
 * favourite — points at nothing, silently, because a name-keyed lookup that
 * misses is indistinguishable from a size that was deleted.
 *
 * The fix is the one the rest of the catalog already uses: a random-UUID `guid`,
 * minted once at write time and stable forever after.
 *
 * ── Why guid-only, with no numeric id ────────────────────────────────────────
 * `ISizePrice.id` is declared but NOTHING in the repository reads it — verified
 * across packages and apps. A numeric id would also mean a counter round-trip
 * PER SIZE on every item save, and the counter exists to make ids unique within
 * a COLLECTION; a size is an array element scoped to one item, so there is no
 * collection for it to be unique in. This is the documented RecipeOps precedent
 * (`Counter.ts`): "operational entities intentionally have NO id range: identity
 * is a randomUUID `guid`, never a numeric counter." `id` is left untouched —
 * this mints identity, it does not remove a field somebody might yet populate.
 */

/**
 * The shape this operates on. Deliberately structural rather than importing
 * `ISizePrice` from `@order-up/models`, since a size row is just a named price
 * wherever it appears (item, group override, import row).
 *
 * No index signature: adding `[key: string]: unknown` would make the readonly
 * `SizePrice` CLASS fail to satisfy it, and every real caller passes exactly
 * that class.
 */
export interface SizePriceIdentityShape {
    guid?: string;
    name: string;
}

/** A v4 UUID, using the platform primitive wherever it exists. */
function mintGuid(): string {
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID();
    }
    // Node 18+ and every browser we support have `crypto.randomUUID`. This
    // branch exists so a stripped test environment cannot make an item save
    // throw — an unlikely-but-possible absence must degrade to a valid UUID,
    // never to `undefined` (which would silently reinstate name-keying).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/**
 * Returns `sizePrices` with a `guid` on every row, minting only where one is
 * absent.
 *
 * IDEMPOTENT AND NON-DESTRUCTIVE BY CONSTRUCTION — this is the whole contract.
 * It runs on every item write, including every update of an item that already
 * has guids. If it re-minted, the identity it exists to establish would change
 * on each save, which is strictly worse than having none: a stable-looking key
 * that silently rotates. A row that already carries a non-empty `guid` is
 * returned untouched, and the array is returned by reference when nothing
 * changed so callers can cheaply detect a no-op.
 *
 * Returns the input unchanged for a nullish/empty array so callers can apply it
 * unconditionally without first asking whether the item is size-priced.
 */
export function ensureSizePriceIdentity<T extends SizePriceIdentityShape>(
    sizePrices: readonly T[] | null | undefined,
): readonly T[] | null | undefined {
    if (!sizePrices || sizePrices.length === 0) return sizePrices;

    let mutated = false;
    const next = sizePrices.map((size) => {
        if (typeof size?.guid === 'string' && size.guid.trim() !== '') {
            return size;
        }
        mutated = true;
        return { ...size, guid: mintGuid() };
    });

    return mutated ? next : sizePrices;
}

/**
 * Resolves an external size reference against an item's sizes.
 *
 * Matches on `guid` OR on the size's name, and that dual match is REQUIRED, not
 * a convenience:
 *
 *  - Menus already published to a marketplace carry NAME-keyed size ids. Those
 *    live on the marketplace's side until the next publish propagates, and
 *    orders placed against them arrive with the old key. A guid-only match would
 *    reject every one of those orders as "size not found" — turning an identity
 *    improvement into a live order-rejection outage.
 *  - Backfill is not instantaneous, and an item saved by an older client can
 *    still arrive without guids.
 *
 * So the question asked is not "is this a guid?" but "does this item have a size
 * that answers to this key?", which is correct under every mix of the two.
 *
 * Name comparison is case-insensitive and whitespace-normalized because the
 * emitted key has been through `partner_id` sanitization (spaces → `-`), and
 * matching must survive that round trip.
 */
export function findSizeByExternalKey<T extends SizePriceIdentityShape>(
    sizePrices: readonly T[] | null | undefined,
    externalKey: string | null | undefined,
): T | undefined {
    if (!sizePrices?.length || !externalKey) return undefined;

    const needle = normalizeSizeKey(externalKey);
    if (needle === '') return undefined;

    return sizePrices.find(
        (size) =>
            (typeof size?.guid === 'string' &&
                normalizeSizeKey(size.guid) === needle) ||
            normalizeSizeKey(size?.name) === needle,
    );
}

/** Lowercase, and collapse the separators `partner_id` sanitization produces. */
function normalizeSizeKey(value: string | null | undefined): string {
    if (typeof value !== 'string') return '';
    return value.trim().toLowerCase().replace(/[\s-]+/g, '-');
}
